/**
 * Public application API helpers — these run UNAUTHENTICATED, so they must
 * not pull in tokens/headers from the auth'd `api` axios instance. We use a
 * small dedicated axios instance with no interceptors.
 */
import axios from 'axios';

const API_BASE =
  (import.meta.env.VITE_PUBLIC_API_URL as string | undefined) ??
  'https://api.dgagentlaunch.com';

export const publicApi = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

export type PublicTokenInfo = {
  ok: boolean;
  tenant: { name: string; slug: string } | null;
  expiresAt: string;
};

export async function validateToken(token: string): Promise<PublicTokenInfo> {
  const { data } = await publicApi.get(`/api/public/applications/${token}`);
  return data;
}

export type UploadField = 'resume' | 'license' | 'voidedCheck';

export type PresignedUpload = {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
  key: string;
  maxSize: number;
};

export async function getUploadUrl(
  token: string,
  body: { field: UploadField; filename: string; mimeType: string; size: number }
): Promise<PresignedUpload> {
  const { data } = await publicApi.post(`/api/public/applications/${token}/upload-url`, body);
  return data;
}

export type SubmitBody = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dob: string;
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
  hasExistingLicense: boolean;
  licenseNumber?: string;
  licenseState?: string;
  compTier: number;
  website?: string; // honeypot
  uploads: Array<{ field: UploadField; s3Key: string; mimeType: string; size: number }>;
};

export async function submitApplication(token: string, body: SubmitBody) {
  const { data } = await publicApi.post(`/api/public/applications/${token}`, body);
  return data;
}
